import { Injectable, inject } from '@angular/core';
import { PaymentTypesEnum } from '@modules/payment/constants/paymentTypes.enum';
import {
  GetEripData,
  GetPaymentClaims,
  GetPaymentMethods,
  PaymentTypes,
  SendCourierPayment,
  SendCurrentPayment,
  SetCurrentPayment,
  SetEripPath,
  SetPaymentMethods,
  SetPayPrecondition,
  SetQrCodeLink,
  SetWebPayFormData,
  SetWebPayRedirectUrl,
} from '@modules/payment/store/payment.action';
import { AppState } from '@modules/shared/store';
import { HideSpinnerLoading } from '@modules/shared/store/common.actions';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { map, switchMap } from 'rxjs/operators';
import { ErrorsService } from '@modules/errors/services/errors.service';
import { SendPaymentData } from '../models/send-payment-data.interface';
import { DefaultService } from '@codegen/service';

@Injectable()
export class PaymentEffects {
  private actions$ = inject(Actions);
  private paymentResourceService = inject(DefaultService);
  private store = inject<Store<AppState>>(Store);
  private errors = inject(ErrorsService);

  public GetPaymentMethods$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetPaymentMethods>(PaymentTypes.GetPaymentMethods),
      map(data => data.payload),
      switchMap(contractId =>
        this.paymentResourceService.contractsPaymentMethods({ contractId })
      ),
      switchMap(res => [new SetPaymentMethods(res.paymentMethods)])
    )
  );

  public GetPaymentClaims$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetPaymentClaims>(PaymentTypes.GetPaymentClaims),
      map(data => data.payload),
      switchMap(contractId =>
        this.paymentResourceService.contractsPaymentClaims({ contractId })
      ),
      switchMap(res => {
        return [
          new SetPayPrecondition({
            currentCurrency: res.currency,
            paymentType: res.paymentType,
            isEditCurrentPayment: res.editableCurrentPayment,
            finalPayment: res.finalPayment,
            currentPayment: res.currentPayment,
            contractNumber: res.contractNumber,
          }),
          new SetCurrentPayment(res.currentPayment),
        ];
      })
    )
  );

  public SendCurrentPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendCurrentPayment>(PaymentTypes.SendCurrentPayment),
      map(data => data.payload),
      switchMap(data => {
        let paymentType;
        this.store
          .select(state => state.payments.payPrecondition.paymentType)
          .subscribe(type => {
            if (type) {
              paymentType = type;
            }
          });
        if (paymentType === PaymentTypesEnum.initialFee) {
          return this.paymentResourceService.paymentFormDataInitialFee({
            contractId: data.contractId,
            amount: data.amount,
          });
        } else {
          return this.paymentResourceService.paymentFormDataRepayment({
            contractId: data.contractId,
            amount: data.amount,
          });
        }
      }),
      switchMap(response => {
        return [
          new SetWebPayFormData(response.formData),
          new SetWebPayRedirectUrl(response.redirectUrl),
        ];
      })
    )
  );

  public GetEripData$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetEripData>(PaymentTypes.GetEripData),
      map(data => data.payload),
      switchMap((contractId: string) =>
        this.paymentResourceService.contractsPaymentClaims({ contractId })
      ),
      switchMap(res => {
        this.store.dispatch(
          new SetPayPrecondition({
            currentCurrency: res.currency,
            paymentType: res.paymentType,
            isEditCurrentPayment: res.editableCurrentPayment,
            finalPayment: res.finalPayment,
            currentPayment: res.currentPayment,
            contractNumber: res.contractNumber,
          })
        );
        return res.paymentType === PaymentTypesEnum.initialFee
          ? this.paymentResourceService.paymentLinkInitialFee({
              contractId: res.contractId,
            })
          : this.paymentResourceService.paymentLinkRepayment({
              contractId: res.contractId,
            });
      }),
      switchMap(res => {
        if (res.errorInfo.error === '10000') {
          this.errors.showError(res, '', {
            actionId: res.actionId || '',
            questionnaireId: res.questionnaireId || '',
          });
          return [new HideSpinnerLoading()];
        }
        return [
          new SetQrCodeLink(res.link),
          new SetEripPath(res.eripPath),
          new HideSpinnerLoading(),
        ];
      })
    )
  );

  public SendCourierPayment$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendCourierPayment>(PaymentTypes.SendCourierPayment),
      map(data => data.payload),
      switchMap((data: SendPaymentData) =>
        this.paymentResourceService.paymentPayCourier({
          contractId: data.contractId,
          amount: data.amount,
        })
      ),
      map(() => new HideSpinnerLoading())
    )
  );
}
